import React, { useEffect, useState, useRef } from 'react';
import './FileUploadPopup.css';
import { API_BASE_URL } from './config';

export const siteTranslationMap = {
  "Upper Lip": "upperLip",
  "Lower Lip": "lowerLip",
  "Left Cheeks (Inside)": "leftCheek",
  "Right Cheeks (Inside)": "rightCheek",
  "Tongue Top": "tongueTop",
  "Tongue Back": "tongueBack",
  "Left Side Tongue": "leftSideTongue",
  "Right Side Tongue": "rightSideTongue",
  "Roof of Mouth": "roofOfMouth",
  "Bottom of Mouth": "bottomOfMouth",
  "Gums": "gums",
  "Back of Throat": "backOfThroat"
};

export const pathologyTranslationMap = {
  "N/A": "n_a",
  "Malignant pathology (SCC)": "malignant_scc",
  "Malignant pathology (other)": "malignant_other",
  "Benign pathology/within normal limits": "benign"
};

export const siteToMaskMap = {
  "Upper Lip": "1._Upper_Lip.svg",
  "Lower Lip": "2_Lower_Lip.svg",
  "Left Cheeks (Inside)": "3_Left_Cheek.svg",
  "Right Cheeks (Inside)": "4_Right_Cheek.svg",
  "Tongue Top": "6_Top_of_Tongue.svg",
  "Tongue Back": "5_Back_of_Tongue.svg",
  "Left Side Tongue": "7_Left_Side_Tongue.svg",
  "Right Side Tongue": "8_Right_Side_Tongue.svg",
  "Roof of Mouth": "9_Roof_of_Mouth.svg",
  "Bottom of Mouth": "10_Bottom_of_Mouth.svg",
  "Gums": "11_Gums.svg",
  "Back of Throat": "12_Back_of_Throat.svg"
};

// Return the source rectangle that matches an object-fit: cover camera preview.
export const getCoverCrop = (sourceWidth, sourceHeight, targetAspectRatio) => {
  const sourceAspectRatio = sourceWidth / sourceHeight;

  if (sourceAspectRatio > targetAspectRatio) {
    const width = sourceHeight * targetAspectRatio;
    return { x: (sourceWidth - width) / 2, y: 0, width, height: sourceHeight };
  }

  const height = sourceWidth / targetAspectRatio;
  return { x: 0, y: (sourceHeight - height) / 2, width: sourceWidth, height };
};


const FileUploadPopup = ({ isOpen, onClose, onFileSelect, site, initialNote = '', userType = 'physician' }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [note, setNote] = useState(initialNote);
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState(Object.entries(siteTranslationMap));
  const [isUploading, setIsUploading] = useState(false);
  const [typeOfCancer, setTypeOfCancer] = useState('');
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraStreamRef = useRef(null);

  const siteToImageMap = {
    "Upper Lip": "upper_lip.png",
    "Lower Lip": "lower_lip.png",
    "Left Cheeks (Inside)": "check_inside.png",
    "Right Cheeks (Inside)": "check_inside.png",
    "Tongue Top": "tongue.png",
    "Tongue Back": "back_of_throat.png",
    "Left Side Tongue": "tongue.png",
    "Right Side Tongue": "tongue.png",
    "Roof of Mouth": "roof_of_mouth.png",
    "Bottom of Mouth": "bottom_of_mouth.png",
    "Gums": "gums.png",
    "Back of Throat": "back_of_throat.png"
  };

  const sampleImage = siteToImageMap[site];
  const maskImage = siteToMaskMap[site];

  useEffect(() => {
    if (!isOpen || !isCameraOpen) return undefined;

    let cancelled = false;
    let stream;
    const videoElement = videoRef.current;

    const startCamera = async () => {
      setCameraError('');
      setIsCameraReady(false);

      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError('Live camera preview is not supported by this browser.');
        return;
      }

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });

        if (cancelled) {
          stream.getTracks().forEach(track => track.stop());
          return;
        }

        cameraStreamRef.current = stream;
        if (videoElement) {
          videoElement.srcObject = stream;
          await videoElement.play();
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Unable to start camera:', error);
          setCameraError('Camera access was unavailable. Check camera permission and try again.');
        }
      }
    };

    startCamera();

    return () => {
      cancelled = true;
      const activeStream = stream || cameraStreamRef.current;
      activeStream?.getTracks().forEach(track => track.stop());
      if (cameraStreamRef.current === activeStream) cameraStreamRef.current = null;
      if (videoElement) videoElement.srcObject = null;
    };
  }, [isCameraOpen, isOpen]);

  const setFileAndPreview = (file) => {
    setSelectedFile(file);

    const fileReader = new FileReader();
    fileReader.onload = () => setPreviewUrl(fileReader.result);
    fileReader.readAsDataURL(file);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFileAndPreview(e.target.files[0]);
      setIsCameraOpen(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleCameraClick = () => {
    setIsCameraOpen(true);
  };

  const handleCameraBack = () => {
    setIsCameraOpen(false);
    setCameraError('');
  };

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth || !video.videoHeight) return;

    const targetAspectRatio = 697 / 361;
    const crop = getCoverCrop(video.videoWidth, video.videoHeight, targetAspectRatio);
    canvas.width = Math.round(crop.width);
    canvas.height = Math.round(crop.height);

    const context = canvas.getContext('2d');
    context.drawImage(
      video,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      0,
      0,
      canvas.width,
      canvas.height,
    );

    canvas.toBlob(blob => {
      if (!blob) {
        setCameraError('The photo could not be captured. Please try again.');
        return;
      }

      const fileName = `${site.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${Date.now()}.jpg`;
      setFileAndPreview(new File([blob], fileName, { type: 'image/jpeg', lastModified: Date.now() }));
      setIsCameraOpen(false);
    }, 'image/jpeg', 0.92);
  };

  const handleUpload = async () => {
    if (selectedFile) {
      if (userType === 'physician' && selectedTags.length === 0) {
        alert('Please select at least one anatomical site.');
        return;
      }
      setIsUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      const tag = userType === 'physician' ? selectedTags.join(',') : site;
      formData.append('tag', tag);
      formData.append('site', site);
      if (note) {
        formData.append('note', note);
      }
      if (typeOfCancer) {
        formData.append('type_of_cancer', typeOfCancer);
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/image/upload/`, {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          const result = await response.json();
          // The upload response should include the persistent path of the image.
          // We ensure this path is passed along with other metadata.
          const resultPayload = {
            ...result,
            type_of_cancer: result.type_of_cancer || typeOfCancer,
          };
          onFileSelect(resultPayload, selectedFile, site, note); // Pass back the enhanced result
          onClose();
        } else {
          console.error('Upload failed');
          // Optionally, show an error message to the user
        }
      } catch (error) {
        console.error('Error uploading file:', error);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleCancel = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
    setIsCameraOpen(false);
    setCameraError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="popup-overlay">
      <div className="popup-container">
        <div className="popup-header">
          <h3>Upload Image for Site {site}</h3>
          <button className="close-button" onClick={handleCancel}>×</button>
        </div>
        
        <div className="popup-content">
          {isCameraOpen && !previewUrl ? (
            <div className="camera-container">
              <div className="camera-stage">
                <video
                  ref={videoRef}
                  className="camera-video"
                  autoPlay
                  muted
                  playsInline
                  onLoadedMetadata={() => setIsCameraReady(true)}
                  aria-label={`Camera preview for ${site}`}
                />
                {maskImage && (
                  <img
                    src={`/mouth_masks/${maskImage}`}
                    alt=""
                    className="camera-mask"
                    aria-hidden="true"
                  />
                )}
              </div>
              <p className="camera-instruction">Align the mouth with the template, then capture the photo.</p>
              {cameraError && <p className="camera-error" role="alert">{cameraError}</p>}
              <div className="camera-controls">
                <button type="button" className="cancel-button" onClick={handleCameraBack}>Back</button>
                {cameraError ? (
                  <button type="button" className="confirm-button" onClick={() => cameraInputRef.current.click()}>
                    Use Device Camera
                  </button>
                ) : (
                  <button type="button" className="capture-button" onClick={handleCapture} disabled={!isCameraReady}>
                    <span aria-hidden="true" className="capture-button-dot" />
                    Capture
                  </button>
                )}
              </div>
              <canvas ref={canvasRef} className="camera-canvas" />
            </div>
          ) : !previewUrl ? (
            <div className="upload-options">
              <div className="upload-option" onClick={handleUploadClick}>
                <div className="option-icon">📁</div>
                <div className="option-label">Upload File</div>
              </div>
              <div className="upload-option" onClick={handleCameraClick}>
                <div className="option-icon">📷</div>
                <div className="option-label">Take Photo</div>
              </div>
              {sampleImage && (
                <div className="sample-image-container">
                  <img src={`/images/${sampleImage}`} alt={`Sample for ${site}`} className="sample-image" />
                  <p className="sample-image-label">Sample Image</p>
                </div>
              )}
            </div>
          ) : (
            <div className="preview-container">
              <div className="uploaded-image-stage">
                <img src={previewUrl} alt={`Preview for ${site}`} className="uploaded-image-preview" />
                {maskImage && (
                  <img
                    src={`/mouth_masks/${maskImage}`}
                    alt=""
                    className="camera-mask"
                    aria-hidden="true"
                  />
                )}
              </div>
              <div className="file-info">
                <p>{selectedFile.name}</p>
                <p>{Math.round(selectedFile.size / 1024)} KB</p>
              </div>
              <div className="note-input-container">
                <label htmlFor="image-note">Note (optional):</label>
                <textarea
                  id="image-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a note about this image..."
                  className="image-note-textarea"
                />
              </div>
              {userType === 'physician' && (
                <>
                <div className="note-input-container">
                  <label htmlFor="type-of-cancer">Type of Cancer:</label>
                  <select
                    id="type-of-cancer"
                    value={typeOfCancer}
                    onChange={(e) => setTypeOfCancer(e.target.value)}
                    className="image-tag-select"
                  >
                    <option value="">-- Select Type of Cancer --</option>
                    {Object.entries(pathologyTranslationMap).map(([label, value]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="note-input-container">
                  <label htmlFor="image-tag">Anatomical Site(s):</label>
                  <select
                    id="image-tag"
                    value=""
                    onChange={(e) => {
                      const newTag = e.target.value;
                      if (newTag && !selectedTags.includes(newTag)) {
                        setSelectedTags([...selectedTags, newTag]);
                        setAvailableTags(availableTags.filter(([label, value]) => value !== newTag));
                      }
                    }}
                    className="image-tag-select"
                  >
                    <option value="" disabled>-- Add a site --</option>
                    {availableTags.map(([label, value]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                  {selectedTags.length > 0 && (
                    <div className="selected-tags-container">
                      <span className="tags-label">Tags:</span>
                      <div className="selected-tags">
                        {selectedTags.map(tag => (
                          <div
                            key={tag}
                            className="selected-tag"
                          >
                            <span>{Object.keys(siteTranslationMap).find(key => siteTranslationMap[key] === tag)}</span>
                            <span
                              className="remove-tag"
                              onClick={() => {
                                setSelectedTags(selectedTags.filter(t => t !== tag));
                                const [label, value] = Object.entries(siteTranslationMap).find(([l, v]) => v === tag);
                                setAvailableTags([...availableTags, [label, value]].sort((a, b) => a[0].localeCompare(b[0])));
                              }}
                            >
                              ×
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                </>
              )}
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={cameraInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </div>
        
        <div className="popup-footer">
          {previewUrl && (
            <>
              <button type="button" className="cancel-button" onClick={handleCancel} disabled={isUploading}>Cancel</button>
              <button type="button" className="confirm-button" onClick={handleUpload} disabled={isUploading}>
                {isUploading ? 'Uploading...' : 'Upload'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileUploadPopup;
